import { Button } from '@/components/ui/button'
import { useSuspenseCurrentMedication } from '@/hooks/useCurrentMedication'
import { useMedicationOptions } from '@/hooks/useMedicationOptions'
import { usePreviousOrderDates } from '@/hooks/usePreviousOrderDates'
import { useToast } from '@/hooks/use-toast'
import { currentRowsByItem } from '@/lib/api/med-requests/mps-sync-plan'
import { takenMedicationValues, toMedicationOption } from '@/lib/medication-options'
import { MPS_MAX_TREATMENTS } from '@/lib/constants/med-requests'
import { useMedicationRequestStore, type ExtraTreatment } from '@/lib/stores/medication-request'
import { getCurrentUKDate, isFutureIssueDate, isMpsMedRequest, isMpsTreatmentLimitReached } from '@/lib/utils'
import type { MedRequest, Medication } from '@/types/data'
import { format } from 'date-fns'
import { Plus } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import TreatmentCard from './treatment-card'

type TreatmentView = {
  key: string
  medication: Medication | null
  direction: string
  issueDate: Date
  onMedicationChange: (medication: Medication, direction: string) => void
  onDirectionChange: (direction: string) => void
  onIssueDateChange: (issueDate: Date) => void
  onRemove?: () => void
}

/**
 * Prescription details for MPS (MultiProduct Subscription) orders: one card per treatment plus
 * an "Add another treatment" action, capped at MPS_MAX_TREATMENTS cards in total.
 *
 * Treatment 1 stays bound to the flat store fields the rest of the page reads (footer gating,
 * post-order view); Treatments 2..N live in `extraTreatments`, seeded once per request from the
 * family so each card carries the subscription's current product as its diff baseline
 * (`persistedMedication` + `itemHash`). Every pick, change and removal here is LOCAL-ONLY —
 * nothing reaches the subscription until the prescriber approves, at which point
 * useSyncMpsTreatmentsOnApprove (called from useApproveMedicationRequest) applies the per-card
 * diffs via the multiproduct endpoints (queued removals first, then adds, then one batched
 * swap; unchanged cards produce no request), and approve still sends no changeProduct on MPS
 * requests. Deleting a seeded card only queues it (`removedTreatments` in the store) for that
 * sync.
 */

/**
 * One extra card per other subscription item, from the item's newest family row (currentRowsByItem):
 * an older order's row would show a dose or medication since changed as a card of its own (CD-374).
 */
const seedTreatmentsFromFamily = (
  medRequest: MedRequest,
  findMedicationByHash: (lookupId: string) => Medication | undefined
): ExtraTreatment[] => {
  const ownMedication = medRequest.orderedItem?.id ? findMedicationByHash(medRequest.orderedItem.id) : undefined
  const ownHashes = new Set(
    [medRequest.orderedItem?.id, ownMedication?.productHash, ownMedication?.variantHash].filter(Boolean)
  )

  return [...currentRowsByItem(medRequest)].flatMap(([itemHash, row]) => {
    if (itemHash === medRequest.mpsHash || ownHashes.has(row.orderedItem.id)) return []

    // Exact variant when the row has a variation hash, product stand-in otherwise. A hash unknown
    // to the catalogue is skipped — the family fan-out still approves its med request.
    const rowMedication = findMedicationByHash(row.orderedItem.id)
    if (!rowMedication) return []

    return {
      id: row.id,
      itemHash,
      medication: rowMedication,
      persistedMedication: rowMedication,
      direction: row.orderedItem.directions || rowMedication.medicationDirections || '',
      issueDate: getCurrentUKDate()
    }
  })
}
export default function MpsPrescriptionDetails({ isEditable }: { isEditable: boolean }) {
  const {
    medicationIssueDate,
    setMedicationIssueDate,
    medicationDirection,
    setMedicationDirection,
    medication,
    setMedication,
    extraTreatments,
    addExtraTreatment,
    updateExtraTreatment,
    removeExtraTreatment,
    mpsSeededRequestId,
    seedMpsTreatments,
    isApproveSubmitting
  } = useMedicationRequestStore()

  const { toast } = useToast()
  const { data: medRequestData } = useSuspenseCurrentMedication()
  const { options, medications, findMedication } = useMedicationOptions()

  // Only mps_med_order rows reach the frontend; any family member seeds the family cards.
  // Subscription mutations key on each row's `mpsHash` (its medication's mirror item on the
  // subscription order); med-req updates use the rows' own ids.
  const isMpsSubscription = isMpsMedRequest(medRequestData)

  const { advancedPreviousOrderDate, lastIssuedDate } = usePreviousOrderDates()

  const isFutureDate = isFutureIssueDate(medicationIssueDate)

  const setMedicationFromMedReq = (lookupId: string) => {
    const medicationData = findMedication(lookupId)
    const medReqDirection = medRequestData.orderedItem.directions

    setMedicationDirection(medReqDirection || medicationData?.medicationDirections || '')
    setMedication(medicationData ?? null)
  }

  // Seed Treatment 1 and the extra cards once per request: the request's own item becomes
  // Treatment 1, and what's on the subscription beyond it becomes the diff baseline the
  // approve sync compares against. The store guard keeps refetches (e.g. after a partially
  // failed approve) from clobbering in-progress edits — an unguarded Treatment 1 re-seed
  // would reset the prescriber's pick to the ordered item, and the retry's plan would then
  // read the swap as "unchanged" and silently drop it.
  useEffect(() => {
    if (!isMpsSubscription || !medications?.length) return
    if (mpsSeededRequestId === medRequestData.id) return

    setMedicationFromMedReq(medRequestData?.orderedItem?.id || '')
    seedMpsTreatments(medRequestData.id, seedTreatmentsFromFamily(medRequestData, findMedication))
  }, [isMpsSubscription, medications, medRequestData, mpsSeededRequestId, seedMpsTreatments, findMedication])

  const treatments: TreatmentView[] = [
    {
      key: 'primary',
      medication,
      direction: medicationDirection,
      issueDate: medicationIssueDate,
      onMedicationChange: (nextMedication, nextDirection) => {
        setMedication(nextMedication)
        setMedicationDirection(nextDirection)
      },
      onDirectionChange: setMedicationDirection,
      onIssueDateChange: setMedicationIssueDate
    },
    ...extraTreatments.map<TreatmentView>(treatment => ({
      key: treatment.id,
      medication: treatment.medication,
      direction: treatment.direction,
      issueDate: treatment.issueDate,
      onMedicationChange: (nextMedication, nextDirection) =>
        updateExtraTreatment(treatment.id, { medication: nextMedication, direction: nextDirection }),
      onDirectionChange: direction => updateExtraTreatment(treatment.id, { direction }),
      onIssueDateChange: issueDate => updateExtraTreatment(treatment.id, { issueDate }),
      // Local-only: a seeded card is queued for the approve sync by the store, not removed
      // from the subscription here.
      onRemove: () => removeExtraTreatment(treatment.id)
    }))
  ]

  // Seeded cards are never truncated — they mirror what's already on the subscription and
  // dropping one would queue nothing for the sync — so a family already at the cap simply can't
  // grow further.
  const isTreatmentLimitReached = isMpsTreatmentLimitReached(extraTreatments)

  // A product-level pick (wooSKU is a product hash) has no row in the swap catalogue, and the
  // combobox renders its trigger label by matching the pick's value against the options — so
  // the cards' own picks are prepended when missing, or every seeded card would sit on
  // "Select an option...". Selecting one back is a no-op product-wise, so it can stay listed.
  const cardOptions = useMemo(() => {
    const seen = new Set(options.map(option => option.value))
    const missingPicks: Medication[] = []

    for (const pick of [medication, ...extraTreatments.map(treatment => treatment.medication)]) {
      if (!pick || seen.has(pick.wooSKU)) continue
      seen.add(pick.wooSKU)
      missingPicks.push(pick)
    }

    return missingPicks.length ? [...missingPicks.map(toMedicationOption), ...options] : options
  }, [options, medication, extraTreatments])

  // A medication picked on one card is disabled on the others (see takenMedicationValues).
  const disabledValuesFor = (current: TreatmentView) =>
    takenMedicationValues(
      treatments.filter(entry => entry.key !== current.key).map(entry => entry.medication),
      medications ?? []
    )

  const handleMedicationChange = (treatment: TreatmentView) => (lookupId: string) => {
    const medicationData = findMedication(lookupId)
    if (!medicationData) throw new Error('Medication not found')

    // OTC items carry no directions, so clear rather than seed from the catalogue.
    const nextDirection = medicationData.type === 'OTC' ? '' : (medicationData.medicationDirections ?? '')
    treatment.onMedicationChange(medicationData, nextDirection)
  }

  const handleSetAdvancedDate = (treatment: TreatmentView) => () => {
    if (!advancedPreviousOrderDate) return

    treatment.onIssueDateChange(advancedPreviousOrderDate)
    toast({
      title: 'Issue date set',
      description: `Issue date set to [${format(advancedPreviousOrderDate, 'dd MMMM yyyy')}]`
    })
  }

  return (
    <div data-testid='prescription-details'>
      <div className='rounded-2xl border border-custom-neutral-200 p-6 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'>
        <p
          id='prescription-details'
          className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] font-feature-settings-liga-off font-feature-settings-clig-off mb-6'
        >
          Prescription Details
        </p>

        <div className='flex flex-col gap-6'>
          {treatments.map((treatment, index) => (
            <TreatmentCard
              key={treatment.key}
              index={index}
              isEditable={isEditable}
              options={cardOptions}
              disabledValues={disabledValuesFor(treatment)}
              medication={treatment.medication}
              direction={treatment.direction}
              issueDate={treatment.issueDate}
              onMedicationChange={handleMedicationChange(treatment)}
              onDirectionChange={treatment.onDirectionChange}
              onIssueDateChange={treatment.onIssueDateChange}
              lastIssuedDate={lastIssuedDate}
              onSetAdvancedDate={advancedPreviousOrderDate ? handleSetAdvancedDate(treatment) : undefined}
              onRemove={treatment.onRemove}
              isRemoveDisabled={isApproveSubmitting}
            />
          ))}
        </div>

        <Button
          type='button'
          disabled={!isEditable || isApproveSubmitting || isTreatmentLimitReached}
          onClick={addExtraTreatment}
          data-testid='add-another-treatment'
          className='mt-6 gap-3 text-base'
        >
          <span className='flex size-6 shrink-0 items-center justify-center rounded-full border border-white'>
            <Plus className='size-4' />
          </span>
          Add another treatment
        </Button>

        {isTreatmentLimitReached && (
          <p data-testid='treatment-limit-note' className='mt-3 text-sm text-custom-neutral-500 leading-[170%]'>
            A subscription can have a maximum of {MPS_MAX_TREATMENTS} treatments.
          </p>
        )}
      </div>

      {isFutureDate && (
        <div className='rounded-2xl mt-4 border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This request is scheduled for approval on <b>{format(medicationIssueDate, 'MMMM d, yyyy')}.</b>
          </p>
        </div>
      )}
    </div>
  )
}
